import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Ofwca} from '../../../shared/interface/ofwca';
import {ReplaySubject} from 'rxjs';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormHelperService} from '../../../shared/services/form-helper.service';
import {OfwcaService} from '../../../shared/services/ofwca.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {HttpErrorResponse} from '@angular/common/http';
import {RegistrationService} from '../../../shared/services/registration.service';
import {Contract} from '../../../shared/interface/contract';
import {ListResponse} from '../../../shared/interface/response/list-response';
import {skip, takeUntil} from 'rxjs/operators';
import {EmailAddress} from "../../../shared/interface/email-address";
import {PartnerService} from "../../../shared/services/partner.service";
import {Partner} from "../../../shared/interface/partner";

export interface ChangePartnerDialogData {
  ofwcaObject: Ofwca;
}

interface NewContractsForm {
  currentContract: Contract;
  newContractControl: FormControl;
}

@Component({
  selector: 'app-change-partner-dialog',
  templateUrl: './change-partner-dialog.component.html',
  styleUrls: ['./change-partner-dialog.component.scss']
})
export class ChangePartnerDialogComponent implements OnInit, OnDestroy {
  changePartnerForm: FormGroup;
  changePartnerFormValid = false;
  withWalletControl: FormControl;
  withCommissionRedirectionsControl: FormControl;
  newPartnerControl: FormControl;
  newContractsArray: FormArray;
  newFacilityControl: FormControl;
  showAllContracts: FormControl;
  ofwcaObject: Ofwca;
  formErrors = {};
  processingRequest = false;
  partnerOfwcaControl:  FormControl;
  newContractForms: NewContractsForm[] = [];
  ofwcaContracts: ReplaySubject<Contract[]> = new ReplaySubject<Contract[]>(1);
  contracts: Contract[] = [];
  newPartnerObject: Partner;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public dialogRef: MatDialogRef<ChangePartnerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChangePartnerDialogData,
    private fb: FormBuilder,
    private formHelper: FormHelperService,
    private ofwcaService: OfwcaService,
    private partnerService: PartnerService,
    private snack: MatSnackBar,
    private registrationService: RegistrationService,
  ) {
    this.ofwcaObject = data.ofwcaObject;
    this.changePartnerForm = fb.group({
      withWallet: this.withWalletControl = fb.control(true),
      withCommissionRedirections: this.withCommissionRedirectionsControl = fb.control(true),
      newPartner: this.newPartnerControl = fb.control('', [Validators.required]),
      newContracts: this.newContractsArray = fb.array([]),
      newFacility: this.newFacilityControl = fb.control('', [Validators.required]),
      partnerOfwca: this.partnerOfwcaControl = fb.control(false),
    });
    this.showAllContracts = fb.control(false);
  }

  ngOnInit(): void {
    this.loadOfwcaContracts();
    this.registrationService.getOfwcaRegistrationContracts(this.ofwcaObject.uuid)
      .subscribe((response: ListResponse<Contract>) => {
        response.items.forEach((contract: Contract) => {
          const newContractControl = this.fb.control('', [Validators.required]);
          this.newContractsArray.push(this.fb.group({
            currentContract: this.fb.control(contract.uuid),
            newContract: newContractControl,
          }));

          this.newContractForms.push({
            currentContract: contract,
            newContractControl,
          });
        });
      });
    this.showAllContracts.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.contractRestart();
      });

    this.changePartnerForm.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.changePartnerFormValid = this.changePartnerForm.valid);

    this.partnerOfwcaControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((partnerOfwca: boolean) => {
        if (partnerOfwca) {
          this.newPartnerControl.reset();
          this.newFacilityControl.reset();
          this.newPartnerControl.disable();
          this.newFacilityControl.disable();
          this.newPartnerControl.setValidators([]);
          this.newFacilityControl.setValidators([]);
        } else {
          this.newPartnerControl.setValidators([Validators.required]);
          this.newFacilityControl.setValidators([Validators.required]);
          this.newPartnerControl.enable();
          this.newFacilityControl.enable();
        }
      });
    this.newPartnerControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.partnerService.get(this.newPartnerControl.value)
        .pipe(takeUntil(this.destroyed$))
          .subscribe((partner: Partner) => {
            this.newPartnerObject = partner;
          });
        this.changePartnerFormValid = false;
        this.newFacilityControl.reset();
        this.contractRestart();
      });
    this.newFacilityControl.valueChanges
      .subscribe(() => {
        this.changePartnerFormValid = false;
      });

    this.withWalletControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((withWallet: boolean) => {
        if (withWallet) {
          this.newContractsArray.enable();
        } else {
          this.newContractsArray.disable();
          this.partnerOfwcaControl.setValue(false);
        }
      });
  }
  contractRestart(): void {
    this.newContractForms.forEach((newContracts: NewContractsForm) => {
      newContracts.newContractControl.reset();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadOfwcaContracts(): void {
    this.ofwcaService.getOfwcaContracts(this.ofwcaObject.uuid)
      .subscribe((response: ListResponse<Contract>) => {
        response.items.forEach((contract: Contract) => {
          this.contracts.push(contract);
          this.ofwcaContracts.next(this.contracts);
        });
        });
  }

  sendForm(): void {
    this.processingRequest = true;
    this.formHelper.removeBackendErrorState(this.changePartnerForm, this.formErrors);
    this.formErrors = {};

    this.ofwcaService.changePartner(this.changePartnerForm.value, this.ofwcaObject.uuid)
      .subscribe(() => {
        this.snack.open('Pomyślnie zaktualizowano obiekt OFWCA', '', {
          duration: 2000,
        });
        this.processingRequest = false;
        this.dialogRef.close();
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;

      if (error.error.error.text){
        this.snack.open(error.error.error.text, '400', {duration: 4000});
        this.dialogRef.close();
      }else {
        this.snack.open('Podczas aktualizacji obiektu OFWCA wystąpił błąd', error.status.toString(), {
          duration: 2000,
        });
      }

        if (error.status === 400) {
          this.formErrors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.changePartnerForm, this.formErrors);
        }
      })
    ;
  }
}
